/**
 * The main provider's (or main site's) line on the analyzer chart (CU-869fmm949, Curro 2026-10-28): always drawn,
 * last so it sits on top, and marked so the chart paints it thick and in the app blue (as the Rank Analyzer marks
 * the main brand). A cap on how many series are listed (the "Ask Iris" chart evidence) keeps the main among them
 * and drops the last competitor instead, so N stays N. Pure: no React, no signals — `node --test` runs it.
 */

const num = (v) => {
	if (v == null || v === '') return null
	const n = Number(v)
	return Number.isFinite(n) ? n : null
}

/**
 * Which reference the server compared against, mirrored from webback `dor_lor/base_svc.py` `filter_no_split`: the
 * main provider only with providers on and no main site; else the main site. `null` with neither.
 */
export function mainReference({ site, provider, byProvider = false } = {}) {
	const s = num(site), p = num(provider)
	if (byProvider && p != null && s == null) return { provider: p }
	if (s != null) return { site: s }
	return null
}

/**
 * Whether the answer was compared by provider: Web & Provider (`groupBySiteProvider`, the only thing DOR / LOR read),
 * and on Brokers its Broker switch too. The Broker switch is a signal shared with the Rank Analyzer and Brokers does
 * not reset it, so it never counts on another page (QA CU-869fmm949 risk 2).
 */
export const providerMode = ({ section, includeProviders = false, brokerProvider = false } = {}) =>
	!!includeProviders || (section === 'brokers' && !!brokerProvider)

const META = new Set(['name', 'nameFilter', 'nameIcon', 'nameMarket', 'isMainRow', 'avgPrice'])

const rowIs = (row, ref) => Object.entries(row || {}).some(([k, cell]) => {
	if (META.has(k) || !cell || typeof cell !== 'object') return false
	const d = cell.price_data
	if (!d || typeof d !== 'object') return false
	return ref.provider != null ? num(d.id_master_provider) === ref.provider : num(d.id_site) === ref.site
})

/** The series names (`trows[].name`, the chart's `dataKey`) of the reference's rows. */
export function mainSeriesKeys(rows, ref) {
	if (!ref || !Array.isArray(rows)) return []
	const site = ref.site != null ? num(ref.site) : null
	const r = ref.provider != null ? { provider: num(ref.provider) } : { site }
	return rows.filter(row => row?.name && rowIs(row, r)).map(row => row.name)
}

const baseKey = (key) => String(key ?? '').replace(/ diff$/, '')

/** Competitors first in their order, the main series last (drawn on top); each flagged `isMain`. A main's Variation
 *  series (`<name> diff`) is the main too. */
export function orderMainLast(series, mainKeys) {
	const mains = new Set(mainKeys || [])
	const flagged = (series || []).map(s => ({ ...s, isMain: mains.has(baseKey(s?.dataKey)) }))
	return [...flagged.filter(s => !s.isMain), ...flagged.filter(s => s.isMain)]
}

/** At most `cap` items in their order, the main ones always kept: the last competitors make room for them. */
export function keepMain(list, cap, isMain) {
	const items = list || []
	if (items.length <= cap) return items
	const mains = items.filter(isMain).slice(0, cap)
	let room = cap - mains.length
	const keep = new Set(mains)
	for (const it of items) {
		if (room <= 0) break
		if (!keep.has(it) && !isMain(it)) { keep.add(it); room-- }
	}
	return items.filter(it => keep.has(it))
}

export const isMainKey = (key, mainKeys) => (mainKeys || []).includes(baseKey(key))
